import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { totpService } from '@/services/totpService'
import { useForceLightMode } from '@/contexts/ThemeContext'
import type { ApiError } from '@/services/api'

const OTP_LENGTH = 6

const VerifyTOTPPage: React.FC = () => {
  useForceLightMode()
  const navigate = useNavigate()

  const inputRefs = useRef<Array<HTMLInputElement | null>>([])
  const digitsRef = useRef<string[]>(Array(OTP_LENGTH).fill(''))
  const setupDoneRef = useRef(false)
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'setup'>('idle')
  const [error, setError] = useState<string | null>(null)
  const [otpAuthURL, setOtpAuthURL] = useState<string | null>(null)

  const [isSetupMode] = useState(() => {
    return localStorage.getItem('totpRequiresSetup') === 'true'
  })

  const partialToken = localStorage.getItem('partialToken')

  useEffect(() => {
    setTimeout(() => inputRefs.current[0]?.focus(), 150)
  }, [])

  useEffect(() => {
    if (isSetupMode && !setupDoneRef.current) {
      setupDoneRef.current = true
      setStatus('setup')
      totpService.setup().then((res) => {
        if (res.success && res.data) {
          setOtpAuthURL((res.data.otpauthUrl || res.data.otpauth_url) ?? null)
          setStatus('idle')
          setTimeout(() => inputRefs.current[0]?.focus(), 150)
        }
      }).catch(() => {
        setError('Gagal memulai setup authenticator')
        setStatus('idle')
      })
    }
  }, [isSetupMode])

  const submitCode = async (code: string) => {
    if (code.length !== OTP_LENGTH) {
      setError(`Kode harus ${OTP_LENGTH} digit`)
      return
    }
    if (!partialToken) {
      setError('Session expired. Silakan login kembali.')
      return
    }

    setStatus('submitting')
    setError(null)

    try {
      if (isSetupMode) {
        const res = await totpService.verifySetup(code)
        if (!res.success) {
          throw new Error(res.error?.message || 'Verifikasi gagal')
        }

        localStorage.removeItem('partialToken')
        localStorage.removeItem('totpRequiresSetup')

        setStatus('success')
        setTimeout(() => {
          window.location.href = '/profile'
        }, 500)
      } else {
        const res = await totpService.verifyLogin(partialToken, code)
        if (!res.success || !res.data) {
          throw new Error(res.error?.message || 'Verifikasi gagal')
        }

        const { user, tokens } = res.data as { user: any; tokens?: { accessToken: string; refreshToken: string } }

        if (!user || !tokens) {
          throw new Error('Invalid response from server')
        }

        localStorage.setItem('user', JSON.stringify(user))
        localStorage.setItem('tokens', JSON.stringify(tokens))
        localStorage.setItem('authToken', tokens.accessToken)

        localStorage.removeItem('partialToken')
        localStorage.removeItem('totpRequiresSetup')

        setStatus('success')
        window.dispatchEvent(new Event('auth-login'))
        setTimeout(() => {
          window.location.href = '/dashboard'
        }, 500)
      }
    } catch (err) {
      const e = err as ApiError
      setStatus('idle')
      digitsRef.current = Array(OTP_LENGTH).fill('')
      inputRefs.current.forEach((el) => { if (el) el.value = '' })
      setTimeout(() => inputRefs.current[0]?.focus(), 100)

      if (e?.code === 'TOTP_INVALID') {
        setError('Kode authenticator salah. Coba lagi.')
      } else if (e?.code === 'TOTP_VERIFY_FAILED') {
        setError('Verifikasi gagal. Coba lagi.')
      } else if (e?.code === 'INVALID_TOKEN') {
        setError('Session expired. Silakan login kembali.')
      } else {
        setError(e?.message || 'Verifikasi gagal. Coba lagi.')
      }
    }
  }

  const setDigit = (idx: number, digit: string) => {
    digitsRef.current[idx] = digit
    const el = inputRefs.current[idx]
    if (el) el.value = digit
  }

  const focusInput = (idx: number) => {
    if (idx >= 0 && idx < OTP_LENGTH) {
      inputRefs.current[idx]?.focus()
    }
  }

  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key >= '0' && e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
      e.preventDefault()
      setDigit(idx, e.key)

      if (idx < OTP_LENGTH - 1) {
        focusInput(idx + 1)
      }

      if (idx === OTP_LENGTH - 1 && digitsRef.current.every((d) => d !== '')) {
        void submitCode(digitsRef.current.join(''))
      }
      return
    }

    if (e.key === 'Backspace') {
      e.preventDefault()
      if (digitsRef.current[idx]) {
        setDigit(idx, '')
      } else if (idx > 0) {
        setDigit(idx - 1, '')
        focusInput(idx - 1)
      }
      return
    }

    if (e.key === 'Delete') {
      e.preventDefault()
      setDigit(idx, '')
      return
    }

    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      focusInput(idx - 1)
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      focusInput(idx + 1)
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
    if (!pasted) return
    e.preventDefault()
    for (let i = 0; i < OTP_LENGTH; i++) {
      setDigit(i, pasted[i] || '')
    }
    const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1)
    focusInput(focusIdx)
    if (pasted.length === OTP_LENGTH) {
      void submitCode(pasted)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('partialToken')
    localStorage.removeItem('totpRequiresSetup')
    localStorage.removeItem('user')
    localStorage.removeItem('tokens')
    localStorage.removeItem('authToken')
    navigate('/login')
  }

  return (
    <div className="min-h-screen flex flex-col items-center pt-32 md:pt-40 bg-slate-50 dark:bg-slate-950 py-12 px-4 relative overflow-hidden" data-testid="verify-totp-page">
      <div className="absolute top-[-10%] right-[-5%] w-[30%] h-[30%] bg-primary/5 rounded-full blur-[100px]" />
      <div className="max-w-md w-full bg-white/70 dark:bg-slate-900/40 backdrop-blur-xl p-10 rounded-[2rem] border border-white dark:border-white/5 shadow-2xl space-y-8 relative z-10">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mb-4">
            <span className="material-symbols-outlined text-3xl text-slate-600 dark:text-slate-300">security</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {isSetupMode ? 'Setup Authenticator' : 'Verifikasi Authenticator'}
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            {isSetupMode
              ? 'Scan QR code di bawah dengan Google Authenticator, lalu masukkan kode 6 digit.'
              : 'Masukkan kode 6 digit dari Google Authenticator Anda.'}
          </p>
        </div>

        {isSetupMode && otpAuthURL && (
          <div className="flex flex-col items-center space-y-4">
            <div className="bg-white p-4 rounded-xl">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(otpAuthURL)}`}
                alt="TOTP QR Code"
                className="w-48 h-48"
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              Buka Google Authenticator, tap +, lalu Scan QR Code
            </p>
          </div>
        )}

        {isSetupMode && !otpAuthURL && status === 'setup' && (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 dark:border-white"></div>
          </div>
        )}

        <div className="flex justify-between gap-2" role="group" aria-label="Kode Authenticator">
          {Array.from({ length: OTP_LENGTH }, (_, idx) => (
            <input
              key={idx}
              ref={(el) => { inputRefs.current[idx] = el }}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              readOnly={status === 'submitting' || status === 'success'}
              aria-label={`Digit ${idx + 1}`}
              onKeyDown={(e) => handleKeyDown(idx, e)}
              onPaste={handlePaste}
              className="flex-1 h-12 max-w-12 text-center text-xl font-semibold border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 caret-transparent"
              data-testid={`totp-digit-${idx}`}
            />
          ))}
        </div>

        {error && (
          <div role="alert" className="text-sm text-red-600 dark:text-red-400 text-center" data-testid="totp-error">
            {error}
          </div>
        )}

        {status === 'success' && (
          <div role="status" className="text-sm text-green-600 text-center" data-testid="totp-success">
            Verifikasi berhasil. Mengalihkan ke dashboard...
          </div>
        )}

        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={handleLogout}
            className="text-sm font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          >
            Kembali ke login
          </button>

          <button
            type="button"
            onClick={() => void submitCode(digitsRef.current.join(''))}
            disabled={status === 'submitting' || status === 'success'}
            className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50"
            data-testid="verify-totp-button"
          >
            {status === 'submitting' ? 'Memverifikasi...' : 'Verifikasi'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default VerifyTOTPPage
